import { AppSidebar } from "@/lib/shadcn/components/sidebar";
import { Navigate, Outlet } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { adminSidebarItems } from "@/pages/adminSidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/lib/shadcn/ui/sidebar";
import ProfileDropdown from "@/lib/molecules/profileDropdown";
import { Toaster } from "@/lib/shadcn/ui/toaster";
import { Toaster as SonnerToaster } from "@/lib/shadcn/ui/sonner";
import { useResetUnavailableFilterValues } from "@/hooks/useResetUnavailableFilterValues";
import { useGetHostHubSyncIssuesQuery, useGetNeedsRefileQuery } from "@/store/hosthub-api";
import { NavSection } from "@/lib/shadcn/components/sidebar/nav-platform";

const TABLET_BREAKPOINT = 1024;

function useIsTabletOrSmaller() {
    const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(typeof window !== "undefined" ? window.innerWidth < TABLET_BREAKPOINT : false);

    useEffect(() => {
        const handleResize = () => {
            setIsTabletOrSmaller(window.innerWidth < TABLET_BREAKPOINT);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isTabletOrSmaller;
}

export default function AdminLayout() {
    const { user } = useAuth();
    const isTabletOrSmaller = useIsTabletOrSmaller();
    useResetUnavailableFilterValues();

    // Live re-file queue size for the sidebar badge. Shares the RTK Query cache
    // with the Re-file review page, so the badge decrements when a row is marked
    // reviewed (mark-reviewed invalidates the `NeedsRefile` tag).
    const { data: needsRefile = [] } = useGetNeedsRefileQuery();

    // hosthub-event-durability/AS-013: the open-wedge count must reach the
    // operator from ANY admin page, so the query runs here rather than on the
    // Sync issues page alone. Undefined — not 0 — until a sync has run and left
    // something wedged: a badge reading "0" trains the operator to ignore it.
    const { data: syncIssues } = useGetHostHubSyncIssuesQuery();
    const wedgedCount = syncIssues?.hasRun === true ? syncIssues.issues.length : 0;

    const sidebarSections: NavSection[] = useMemo(
        () =>
            adminSidebarItems.map((section) => ({
                ...section,
                items: section.items.map((item) => ({
                    ...item,
                    items: item.items?.map((subItem) => {
                        if (subItem.url === "/admin/refile-review") {
                            return { ...subItem, badgeCount: needsRefile.length };
                        }
                        if (subItem.url === "/admin/sync-issues") {
                            return { ...subItem, badgeCount: wedgedCount > 0 ? wedgedCount : undefined };
                        }
                        return subItem;
                    }),
                })),
            })),
        [needsRefile.length, wedgedCount],
    );

    if (!user) {
        return <Navigate to="/login" />;
    }

    return (
        <SidebarProvider defaultOpen={!isTabletOrSmaller}>
            <AppSidebar
                sections={sidebarSections}
                user={{
                    name: user.givenName,
                    email: user.email,
                    avatar: user.profileImageUri,
                }}
            />
            <SidebarInset>
                <header className="flex justify-between h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
                    <div className="flex items-center gap-2 px-4">
                        <SidebarTrigger className="-ml-1" />
                    </div>
                    <div className="mr-7">
                        <ProfileDropdown profileImageUrl={user?.profileImageUri} userName={user?.givenName || ""} isAdmin />
                    </div>
                </header>
                <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
                    <main className="p-1">
                        <Outlet />
                        <Toaster />
                        <SonnerToaster />
                    </main>
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
}
